import { Injectable, inject, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  GradingSession,
  SendMessageResponse,
  MessageStreamEvent,
  GradingResultDetail,
  GradingRun,
  MaterialsAnalysisPreview,
  ScoringGuidePreview,
  ScoringSectionPreview,
  ScoringGuideStandard,
  GradingListItem,
  GradingListResponse,
} from '../grading.types';

/**
 * Raw scoring guide response from backend API
 * This has a flat structure, not nested like ScoringGuidePreview
 */
interface ScoringGuideApiResponse {
  id: string;
  status: string;
  grade: number | null;
  usState: string | null;
  topic: string | null;
  sections: ScoringSectionPreview[];
  standards: ScoringGuideStandard[];
  generationCount: number;
  isSavedTemplate: boolean;
  templateName: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class GradingApiService {
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);
  private readonly apiUrl = `${environment.apiUrl}/grading`;

  /**
   * List gradings for the authenticated user with pagination
   * Optionally filter by source ('chat' or 'assignment')
   */
  listGradings(page = 1, limit = 10, source?: string): Observable<GradingListResponse> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    if (source) {
      params['source'] = source;
    }
    return this.http
      .get<ApiResponse<GradingListResponse>>(this.apiUrl, { params })
      .pipe(map((res) => res.data));
  }

  /**
   * Create a new grading session
   * Returns session with initial AI greeting message
   */
  createGrading(): Observable<GradingSession> {
    return this.http
      .post<ApiResponse<GradingSession>>(this.apiUrl, {})
      .pipe(map((res) => res.data));
  }

  /**
   * Get a grading session by ID
   */
  getGrading(id: string): Observable<GradingSession> {
    return this.http
      .get<ApiResponse<GradingSession>>(`${this.apiUrl}/${id}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Send a message and start AI response generation
   * Returns immediately with groupId - use streamMessages to receive updates
   */
  sendMessage(
    gradingId: string,
    content: string,
    resourceIds?: string[],
  ): Observable<SendMessageResponse> {
    const body: { content: string; resourceIds?: string[] } = { content };
    if (resourceIds && resourceIds.length > 0) {
      body.resourceIds = resourceIds;
    }
    return this.http
      .post<ApiResponse<SendMessageResponse>>(
        `${this.apiUrl}/${gradingId}/messages`,
        body,
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Get a grading result by ID
   * Also includes question analysis for the grading session
   */
  getGradingResult(resultId: string): Observable<GradingResultDetail> {
    return this.http
      .get<ApiResponse<GradingResultDetail>>(`${this.apiUrl}/results/${resultId}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get a grading run by ID
   * Returns full run data with student results and statistics
   */
  getGradingRun(runId: string): Observable<GradingRun> {
    return this.http
      .get<ApiResponse<GradingRun>>(`${this.apiUrl}/runs/${runId}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get materials analysis preview for a grading session
   * Returns enriched data with resource previews
   */
  getMaterialsAnalysisPreview(gradingId: string): Observable<MaterialsAnalysisPreview | null> {
    return this.http
      .get<ApiResponse<MaterialsAnalysisPreview | null>>(`${this.apiUrl}/${gradingId}/materials-analysis`)
      .pipe(map((res) => res.data));
  }

  /**
   * Get scoring guide by ID
   * Returns the full scoring guide data transformed to ScoringGuidePreview
   */
  getScoringGuide(scoringGuideId: string): Observable<ScoringGuidePreview> {
    return this.http
      .get<ApiResponse<ScoringGuideApiResponse>>(`${environment.apiUrl}/scoring-guides/${scoringGuideId}`)
      .pipe(
        map((res) => this.transformScoringGuideResponse(res.data)),
      );
  }

  /**
   * Transform backend scoring guide response to frontend ScoringGuidePreview format
   */
  private transformScoringGuideResponse(data: ScoringGuideApiResponse): ScoringGuidePreview {
    // Calculate summary from sections
    let totalQuestions = 0;
    let totalPoints = 0;
    const itemTypes = { rubric: 0, logic: 0, exact_match: 0 };

    for (const section of data.sections || []) {
      for (const item of section.items || []) {
        totalQuestions++;
        // Count item types
        if (item.type in itemTypes) {
          itemTypes[item.type as keyof typeof itemTypes]++;
        }
        // Calculate max points from criteria levels
        for (const criteria of item.criteria || []) {
          const maxLevel = criteria.levels?.[criteria.levels.length - 1];
          if (maxLevel) {
            totalPoints += maxLevel.points ?? 0;
          }
        }
      }
    }

    // Count standards aligned (unique standards referenced in criteria)
    const standardIds = new Set<string>();
    for (const section of data.sections || []) {
      for (const item of section.items || []) {
        for (const criteria of item.criteria || []) {
          if (criteria.standard?.standardId) {
            standardIds.add(criteria.standard.standardId);
          }
        }
      }
    }

    return {
      scoringGuideId: data.id,
      scoringGuide: {
        topic: data.topic || 'Scoring Guide',
        sections: data.sections || [],
        standards: data.standards || [],
      },
      summary: {
        totalQuestions,
        totalPoints,
        standardsAligned: standardIds.size,
        itemTypes,
      },
    };
  }

  /**
   * Stream message updates via SSE for a specific generation cycle
   * Connect to this after sending a message to receive real-time updates
   *
   * Features automatic reconnection with exponential backoff on connection failures.
   *
   * @param gradingId - The grading session ID
   * @param groupId - The generation cycle group ID (returned from sendMessage)
   * @param maxRetries - Maximum number of reconnection attempts (default: 10)
   * @returns Observable that emits message stream events
   */
  streamMessages(
    gradingId: string,
    groupId: string,
    maxRetries = 10,
  ): Observable<MessageStreamEvent> {
    return new Observable<MessageStreamEvent>((subscriber) => {
      const url = `${this.apiUrl}/${gradingId}/messages/stream?groupId=${groupId}`;
      let eventSource: EventSource | null = null;
      let retryCount = 0;
      let retryTimeout: ReturnType<typeof setTimeout> | null = null;
      let isCompleted = false;
      let isUnsubscribed = false;

      const connect = () => {
        if (isUnsubscribed || isCompleted) {
          return;
        }

        eventSource = new EventSource(url);

        eventSource.onmessage = (event) => {
          // Reset retry count on successful message
          retryCount = 0;

          // Run inside Angular zone to trigger change detection
          this.ngZone.run(() => {
            try {
              const streamEvent = JSON.parse(event.data) as MessageStreamEvent;
              subscriber.next(streamEvent);

              // Complete when stream is done or errored
              if (streamEvent.type === 'done' || streamEvent.type === 'error') {
                isCompleted = true;
                eventSource?.close();
                if (streamEvent.type === 'error') {
                  subscriber.error(new Error(streamEvent.error || 'Stream error'));
                } else {
                  subscriber.complete();
                }
              }
            } catch (error) {
              isCompleted = true;
              subscriber.error(error);
              eventSource?.close();
            }
          });
        };

        eventSource.onerror = () => {
          this.ngZone.run(() => {
            eventSource?.close();

            // Don't retry if already completed or unsubscribed
            if (isCompleted || isUnsubscribed) {
              return;
            }

            // Check if we should retry
            if (retryCount < maxRetries) {
              retryCount++;
              // Exponential backoff: 1s, 2s (capped at 2s)
              const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 2000);
              console.warn(
                `SSE connection failed, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`,
              );
              retryTimeout = setTimeout(() => {
                connect();
              }, delay);
            } else {
              // Max retries exceeded
              console.error(
                `SSE connection failed after ${maxRetries} retry attempts`,
              );
              subscriber.error(
                new Error(`SSE connection failed after ${maxRetries} retry attempts`),
              );
            }
          });
        };
      };

      // Start initial connection
      connect();

      // Cleanup on unsubscribe
      return () => {
        isUnsubscribed = true;
        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }
        eventSource?.close();
      };
    });
  }
}
